/**
 * Another page, live, in a frame: the deck, the onboarding demo, the hosted starter. A bar names what's showing,
 * switches desk, tablet and phone widths, opens it in its own tab and goes full screen. `srcDoc` draws a whole
 * document the page already holds (the deck, fetched behind sign-in); `src` loads an address.
 */
import { useRef, useState, type ReactNode } from 'react'
import { cx } from '../cx'
import { IconButton } from '../primitives/Button'
import { Segmented } from '../primitives/Segmented'
import s from './Embed.module.css'

export type EmbedSize = 'desk' | 'tablet' | 'phone'

const SIZE_LABEL: Record<EmbedSize, string> = { desk: 'Desk', tablet: 'Tablet', phone: 'Phone' }

export function Embed({
  title,
  src,
  srcDoc,
  address,
  sizes = ['desk', 'tablet', 'phone'],
  initial = 'desk',
  height = 'md',
  actions,
  openable = true,
}: {
  /** What the frame shows; read by screen readers and shown in the bar when there's no address. */
  title: string
  src?: string
  srcDoc?: string
  /** The address to show in the bar, when it differs from `src` (a path, a friendlier name). */
  address?: string
  /** Widths to offer. One size hides the switch. */
  sizes?: EmbedSize[]
  initial?: EmbedSize
  /** The frame's height: `md` for a page, `tall` for a phone-first demo, `deck` for a 16:9 deck at desk width. */
  height?: 'md' | 'tall' | 'deck'
  /** Controls beside the size switch: a persona picker, a Restart. */
  actions?: ReactNode
  /** Offer "Open in a new tab" (with `src`). Off for pages that run only inside this frame. */
  openable?: boolean
}) {
  const [size, setSize] = useState<EmbedSize>(sizes.includes(initial) ? initial : sizes[0]!)
  const stage = useRef<HTMLDivElement>(null)
  const frame = useRef<HTMLIFrameElement>(null)
  const shown = address ?? src ?? title
  return (
    <div className={s.embed}>
      <div className={s.bar}>
        <span className={s.address} title={shown}>
          {shown}
        </span>
        <div className={s.tools}>
          {actions}
          {sizes.length > 1 && <Segmented size="sm" label={`${title}: width`} options={sizes.map((v) => ({ value: v, label: SIZE_LABEL[v] }))} value={size} onChange={setSize} />}
          {src && openable && <IconButton size="sm" icon="out" label="Open in a new tab" onClick={() => window.open(src, '_blank', 'noopener,noreferrer')} />}
          <IconButton size="sm" icon="full" label="Full screen" onClick={() => void stage.current?.requestFullscreen?.()} />
        </div>
      </div>
      <div ref={stage} className={cx(s.stage, s[`h-${height}`])}>
        <iframe ref={frame} className={cx(s.frame, s[size])} title={title} src={src} srcDoc={srcDoc} loading="lazy" referrerPolicy="no-referrer" />
      </div>
    </div>
  )
}
